/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import useSWR from "swr"
import { useRouter } from "next/router"
import { useAuthReady } from "./useAuthReady"
import {
  ResponseShopGetList,
  ResponseShopGetOne,
  ResponseUserGetList,
  Shop,
  User,
} from "../libs/api/backend/src"
import {
  UrlShopGetAllShopsForUserAPI,
  UrlShopGetAllUsersInShopAPI,
  UrlShopGetAvailableToAddUserToShopAPI,
  UrlShopGetOneAPI,
} from "../libs/api/url"

export function useShopId(): string {
  let shopId = ""

  try {
    const router = useRouter()
    shopId = (router.query.shopId as string) || ""
  } catch (err) {
    console.error(err)
  }

  return shopId
}

export function useShopGetOne(enterprise_id?: string, shop_id?: string) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseShopGetOne>(
    isAuthenticated && enterprise_id && shop_id
      ? UrlShopGetOneAPI(enterprise_id, shop_id)
      : null
  )

  const shop = apiResponse && (apiResponse.data as Shop)

  return {
    shop: apiResponse && shop ? shop : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated && enterprise_id && shop_id && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useShopGetAllShopsForUser(enterprise_id?: string) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseShopGetList>(
    isAuthenticated && enterprise_id
      ? UrlShopGetAllShopsForUserAPI(enterprise_id)
      : null
  )

  const shops = apiResponse && (apiResponse.data as Array<Shop>)

  return {
    shops: apiResponse?.data ?? null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated && enterprise_id && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useShopGetAllUsersInShopAPI(
  enterprise_id?: string,
  shop_id?: string
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseUserGetList>(
    isAuthenticated && enterprise_id && shop_id
      ? UrlShopGetAllUsersInShopAPI(enterprise_id, shop_id)
      : null
  )

  const users = apiResponse && (apiResponse.data as Array<User>)

  return {
    users: apiResponse?.data ?? null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated && enterprise_id && shop_id && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useShopAvailableUsersToAdd(
  enterprise_id?: string,
  shop_id?: string
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseUserGetList>(
    isAuthenticated && enterprise_id && shop_id
      ? UrlShopGetAvailableToAddUserToShopAPI(enterprise_id, shop_id)
      : null
  )

  const users = apiResponse && (apiResponse.data as Array<User>)

  return {
    users: apiResponse?.data ?? null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated && enterprise_id && shop_id && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}
